import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

export interface FeedStory {
  slug: string;
  showSlug?: string;
  reason: string;
  score: number;
  title?: string;
  showName?: string;
  clusterId?: string | null;
  clusterDescription?: string | null;
  similarityBand?: string;
}

// Matches PipelineDiagnostics from shared/src/recommendation.ts
interface Diagnostics {
  vectors: {
    path: string;
    activeCount: number;
    probationCount: number;
    specificity: number | null;
    perVector: Array<{
      strength: number;
      effectiveStrength: number;
      share: number;
      poolSize: number;
      clusterLabel: string | null;
      clusterMatchScore: number | null;
    }>;
  };
  userPrefs: {
    mutedShows: string[];
    followedShows: string[];
    listenHistoryCount: number;
  };
  candidates: {
    source: string;
    poolSize: number;
    similarityRange: { min: number; max: number } | null;
  };
  hardFilters: {
    before: number;
    after: number;
    muted: number;
    languageMissing: number;
    cooldownPermanent: number;
    cooldownTemporary: number;
  };
  scoring: Array<{
    slug: string;
    showSlug: string;
    baseSimilarity: number;
    followBoost: number;
    freshnessBoost: number;
    repeatPenalty: number;
    finalScore: number;
    reason: string;
  }>;
  gapEnforcement: {
    before: number;
    after: number;
    clusterGap: number;
    showGap: number;
    relaxationUsed: boolean;
  };
  similarityBands: {
    highThreshold: number;
    midThreshold: number;
  };
  weightSet: { id: string };
  latencyMs?: number;
}

export const StoryCell = ({
  slug,
  title,
  showName,
}: {
  slug: string;
  title?: string;
  showName?: string;
}) => {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <img
        src={`${apiBaseUrl}/drops/${slug}/image?size=50`}
        alt=""
        width={50}
        height={50}
        style={{ borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
      />
      <Box sx={{ minWidth: 0 }}>
        {showName && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {showName}
          </Typography>
        )}
        <Typography variant="body2" noWrap>
          {title || slug}
        </Typography>
      </Box>
    </Box>
  );
};

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="subtitle2"
    sx={{ mt: 2, mb: 0.5, fontWeight: 'bold', color: 'text.secondary' }}
  >
    {children}
  </Typography>
);

const StatRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Typography variant="caption" display="block" sx={{ py: 0.1, lineHeight: 1.4 }}>
    <Box component="span" sx={{ color: 'text.secondary' }}>{label}:</Box>{' '}
    <Box component="span" sx={{ fontFamily: 'monospace' }}>{value}</Box>
  </Typography>
);

/** Step-by-step waterfall showing how candidates get filtered down */
const FunnelWaterfall = ({
  candidatePoolSize,
  hardFilters,
  gapEnforcement,
}: {
  candidatePoolSize: number;
  hardFilters: Diagnostics['hardFilters'];
  gapEnforcement: Diagnostics['gapEnforcement'];
}) => {
  // Build waterfall steps: each step shows the running total and what was removed
  // Filter order matches applyHardFiltersDetailed: muted → language → cooldown
  const steps: Array<{ count: number; removed: number; label: string }> = [];
  let running = candidatePoolSize;

  steps.push({ count: running, removed: 0, label: 'candidates' });

  if (hardFilters.muted > 0) {
    running -= hardFilters.muted;
    steps.push({ count: running, removed: hardFilters.muted, label: 'muted shows removed' });
  }
  if (hardFilters.languageMissing > 0) {
    running -= hardFilters.languageMissing;
    steps.push({ count: running, removed: hardFilters.languageMissing, label: 'wrong language removed' });
  }
  if (hardFilters.cooldownPermanent > 0) {
    running -= hardFilters.cooldownPermanent;
    steps.push({ count: running, removed: hardFilters.cooldownPermanent, label: 'already listened removed' });
  }
  if (hardFilters.cooldownTemporary > 0) {
    running -= hardFilters.cooldownTemporary;
    steps.push({ count: running, removed: hardFilters.cooldownTemporary, label: 'in cooldown removed' });
  }

  // Gap enforcement: selects from scored candidates while enforcing cluster + show gaps
  const gapLabel = [`${gapEnforcement.after} selected`];
  if (gapEnforcement.relaxationUsed) gapLabel.push('gaps relaxed');
  steps.push({ count: gapEnforcement.after, removed: 0, label: gapLabel.join(', ') });

  return (
    <Box sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
      {steps.map((step, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.15 }}>
          <Box
            sx={{
              width: 32,
              textAlign: 'right',
              fontWeight: i === 0 || i === steps.length - 1 ? 'bold' : 'normal',
              color: i === steps.length - 1 ? 'success.main' : undefined,
            }}
          >
            {step.count}
          </Box>
          <Box sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            {i === 0 ? step.label : step.removed > 0
              ? `\u2212${step.removed} ${step.label}`
              : step.label}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

function vectorPathTooltip(path: string): string {
  switch (path) {
    case 'multi_vector':
      return 'Retrieving from this user’s active interest vectors (one ANN query per vector, fanned out and merged).';
    case 'cold_start':
      return 'No active interest vectors — user has no seeding/behavior signal yet. Showing recent diverse stories instead of personalized results.';
    case 'personalization_disabled':
      return 'The interest-profiles-enabled remote config is off — interest vectors are ignored and recent diverse stories are shown with explicit filtering/boosting only.';
    default:
      return path;
  }
}

const ClusterSummary = ({ stories }: { stories: { clusterId?: string | null }[] }) => {
  const counts = new Map<string, number>();
  for (const s of stories) {
    if (s.clusterId) counts.set(s.clusterId, (counts.get(s.clusterId) ?? 0) + 1);
  }
  if (counts.size === 0) return null;
  const total = stories.length;
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  return (
    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>
      Clusters: {sorted.map(([id, count]) => `${id}:${Math.round(count / total * 100)}%`).join(' | ')}
    </Typography>
  );
};

const SimulationReport = ({ diagnostics, feedStories }: { diagnostics: Diagnostics; feedStories: FeedStory[] }) => {
  const { vectors, userPrefs, candidates, hardFilters, gapEnforcement, similarityBands } =
    diagnostics;

  return (
    <Box sx={{ mt: 1 }}>
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <Tooltip
          title={vectorPathTooltip(vectors.path)}
          arrow
          placement="bottom"
        >
          <Chip
            size="small"
            label={`Vectors: ${vectors.path}`}
            color={vectors.path === 'cold_start' ? 'warning' : 'default'}
          />
        </Tooltip>
        <Chip
          size="small"
          label={`Weights: ${diagnostics.weightSet.id}`}
        />
        {diagnostics.latencyMs !== undefined && (
          <Chip size="small" label={`${diagnostics.latencyMs}ms`} />
        )}
        <Chip
          size="small"
          label={`${vectors.activeCount} active${vectors.probationCount > 0 ? ` + ${vectors.probationCount} probation` : ''}`}
          variant="outlined"
        />
      </Box>

      {/* Funnel — waterfall showing each step's impact */}
      <SectionHeader>Pipeline Funnel</SectionHeader>
      <FunnelWaterfall
        candidatePoolSize={candidates.poolSize}
        hardFilters={hardFilters}
        gapEnforcement={gapEnforcement}
      />

      {/* User prefs */}
      <SectionHeader>User Preferences</SectionHeader>
      <StatRow
        label="Muted shows"
        value={userPrefs.mutedShows.length > 0 ? userPrefs.mutedShows.join(', ') : 'none'}
      />
      <StatRow
        label="Followed shows"
        value={userPrefs.followedShows.length > 0 ? userPrefs.followedShows.join(', ') : 'none'}
      />
      <StatRow label="Listen history entries" value={userPrefs.listenHistoryCount} />

      {/* Interest vectors info */}
      <SectionHeader>Interest Vectors</SectionHeader>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        See the <strong>Interests</strong> tab for full per-vector detail (nearest stories, decay).
      </Typography>
      <StatRow label="Path" value={vectors.path} />
      <StatRow label="Active" value={vectors.activeCount} />
      <StatRow label="Probation" value={vectors.probationCount} />
      <StatRow label="Seeding specificity" value={vectors.specificity ?? 'n/a'} />
      {vectors.perVector.map((v, i) => (
        <StatRow
          key={i}
          label={`Vector ${i + 1}`}
          value={
            <>
              {v.clusterLabel ? (
                <>
                  &ldquo;{v.clusterLabel}&rdquo;{' '}
                  <Box
                    component="span"
                    sx={{ color: (v.clusterMatchScore ?? 0) < 0.5 ? 'warning.main' : 'text.secondary' }}
                  >
                    ({v.clusterMatchScore})
                  </Box>
                  {' — '}
                </>
              ) : null}
              strength {v.strength} (effective {v.effectiveStrength}) — share {Math.round(v.share * 100)}% — pool {v.poolSize}
            </>
          }
        />
      ))}
      {candidates.similarityRange && (
        <StatRow
          label="Similarity range"
          value={`${candidates.similarityRange.min} — ${candidates.similarityRange.max}`}
        />
      )}
      <StatRow
        label="Similarity bands"
        value={`high ≥ ${similarityBands.highThreshold}, mid ≥ ${similarityBands.midThreshold}`}
      />

      {/* Gap enforcement */}
      <SectionHeader>Gap Enforcement</SectionHeader>
      <StatRow label="Cluster gap" value={gapEnforcement.clusterGap} />
      <StatRow label="Show gap" value={gapEnforcement.showGap} />
      <StatRow label="Relaxation used" value={gapEnforcement.relaxationUsed ? 'yes' : 'no'} />

      {/* Feed preview — the actual stream the user would hear */}
      {feedStories.length > 0 && (
        <>
          <SectionHeader>User Stream</SectionHeader>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5, width: 24 }}>#</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Story</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Cluster</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }} align="right">Similarity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feedStories.map((story, i) => (
                  <TableRow key={story.slug}>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5, color: 'text.secondary', verticalAlign: 'top' }}>
                      {i + 1}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <StoryCell
                        slug={story.slug}
                        title={story.title}
                        showName={story.showName}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5, verticalAlign: 'top' }}>
                      {story.clusterId && (
                        <Box>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {story.clusterId}
                          </Typography>
                          {story.clusterDescription && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                              {story.clusterDescription}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.5, verticalAlign: 'top' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: story.score >= 0.9 ? 'bold' : undefined,
                        }}
                      >
                        {story.score}
                      </Typography>
                      {story.similarityBand && (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{
                            fontSize: '0.65rem',
                            color: story.similarityBand === 'high' ? 'success.main'
                              : story.similarityBand === 'mid' ? 'warning.main'
                              : 'text.secondary',
                          }}
                        >
                          {story.similarityBand}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <ClusterSummary stories={feedStories} />
        </>
      )}
    </Box>
  );
};

export { SimulationReport };
export type { Diagnostics };
